import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import type { AuthState } from '../types';
import { getApiUrl } from '../config/api';

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  verify2FA: (code: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    requires2FA: false,
  });

  useEffect(() => {
    // Check for existing session on mount
    const checkSession = async () => {
      try {
        const response = await fetch(getApiUrl('/api/auth/session'), {
          credentials: 'include',
        });
        if (response.ok) {
          const data = await response.json();
          setAuthState({
            user: data.user,
            isAuthenticated: true,
            requires2FA: false,
          });
        }
      } catch (error) {
        console.error('Session check failed:', error);
      }
    };
    checkSession();
  }, []);

  const login = async (email: string, password: string) => {
    const url = getApiUrl('/api/auth/login');
    console.log('Attempting login to:', url);

    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password }),
    });

    console.log('Login response status:', response.status);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('Login error:', errorData);
      throw new Error(errorData.error || 'Login failed');
    }

    const data = await response.json();

    if (data.requires2FA) {
      setAuthState({
        user: null,
        isAuthenticated: false,
        requires2FA: true,
      });
    } else {
      setAuthState({
        user: data.user,
        isAuthenticated: true,
        requires2FA: false,
      });
    }
  };

  const verify2FA = async (code: string) => {
    const response = await fetch(getApiUrl('/api/auth/verify-2fa'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ code }),
    });

    if (!response.ok) {
      throw new Error('2FA verification failed');
    }

    const data = await response.json();
    setAuthState({
      user: data.user,
      isAuthenticated: true,
      requires2FA: false,
    });
  };

  const logout = async () => {
    await fetch(getApiUrl('/api/auth/logout'), {
      method: 'POST',
      credentials: 'include',
    });

    setAuthState({
      user: null,
      isAuthenticated: false,
      requires2FA: false,
    });
  };

  return (
    <AuthContext.Provider value={{ ...authState, login, verify2FA, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
